import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TextReveal } from '../PageTransition';
import { RealWoodenFrame } from './WoodenFrames';

const HandmadeSection = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slideshowImages = [
    { src: "https://customer-assets.emergentagent.com/job_55f4b5e7-2baa-4587-a5f6-01a3193241e9/artifacts/h14ut8vc_tandoorichickenin18.jpg", alt: "Tandoori Oven Cooking" },
    { src: "https://customer-assets.emergentagent.com/job_dece6b44-e465-4355-b189-3021eae05551/artifacts/kdi5scmj_side-view-champignon-frying-with-smoke-fire-human-stove%20%281%29.jpg", alt: "Chef cooking with fire" },
    { src: "https://customer-assets.emergentagent.com/job_dece6b44-e465-4355-b189-3021eae05551/artifacts/fd35plzw_side-view-mushroom-frying-with-gas-stove-fire-human-hand-pan.jpg", alt: "Wok cooking with flames" },
    { src: "https://customer-assets.emergentagent.com/job_dece6b44-e465-4355-b189-3021eae05551/artifacts/po6waqfl_side-view-set-shish-kebab-baked-clay-oven.jpg", alt: "Kebabs in traditional clay oven" }
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide((prev) => (prev + 1) % slideshowImages.length), 4000);
    return () => clearInterval(timer);
  }, [slideshowImages.length]);
  
  const handmadeItems = [
    { name: t('handmade.items.sauces'), desc: t('handmade.items.saucesDesc') },
    { name: t('handmade.items.yogurt'), desc: t('handmade.items.yogurtDesc') },
    { name: t('handmade.items.mayonnaise'), desc: t('handmade.items.mayonnaiseDesc') },
    { name: t('handmade.items.naanDough'), desc: t('handmade.items.naanDoughDesc') },
    { name: t('handmade.items.cottageCheese'), desc: t('handmade.items.cottageCheeseDesc') },
  ];

  return (
    <section className="relative py-16 md:py-24 bg-[#2F2F2F] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="relative max-w-sm mx-auto">
              <AnimatePresence mode="wait">
                <motion.div key={currentSlide} initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
                  <RealWoodenFrame src={slideshowImages[currentSlide].src} alt={slideshowImages[currentSlide].alt} isLoaded={true} />
                </motion.div>
              </AnimatePresence>
              <div className="flex justify-center gap-2 mt-4">
                {slideshowImages.map((_, index) => (
                  <button key={index} onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-[#C97A2B] w-6' : 'bg-white/30 hover:bg-white/50'}`}
                    aria-label={`Go to slide ${index + 1}`} />
                ))}
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="text-center lg:text-left mb-8">
              <TextReveal><p className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase mb-3">{t('handmade.subtitle')}</p></TextReveal>
              <TextReveal delay={0.1}>
                <h2 className="text-3xl md:text-4xl lg:text-5xl text-white mb-3" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                  {t('handmade.title').split(' ').map((word, i) => 
                    word === 'Handmade' || word === 'Käsintehty' || word === 'Handgjort' ? <span key={i}><span className="italic">{word}</span></span> : <span key={i}>{word} </span>
                  )}
                </h2>
              </TextReveal>
              <TextReveal delay={0.2}>
                <p className="text-white/70 text-sm md:text-base max-w-md mx-auto lg:mx-0">
                  {t('handmade.description').split('pure smoky taste').map((part, i, arr) => 
                    i < arr.length - 1 ? <span key={i}>{part}<span className="text-[#C97A2B]">pure smoky taste</span></span> : part
                  )}
                </p>
              </TextReveal>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-8">
              {handmadeItems.map((item, index) => (
                <motion.div key={item.name} className="text-center group"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08, duration: 0.5 }}>
                  <motion.div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-2 rounded-full border border-[#C97A2B]/40 flex items-center justify-center group-hover:border-[#C97A2B] group-hover:bg-[#C97A2B]/10 transition-all duration-300" whileHover={{ scale: 1.1 }}>
                    <span className="text-[#C97A2B] text-lg">✦</span>
                  </motion.div>
                  <h3 className="text-white text-sm md:text-base mb-1 group-hover:text-[#C97A2B] transition-colors" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{item.name}</h3>
                  <p className="text-white/40 text-[10px] md:text-xs font-light leading-tight hidden md:block">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div className="flex flex-wrap justify-center lg:justify-start gap-3" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}>
              <span className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/80 text-xs tracking-wide">{t('handmade.tags.fireCooking')}</span>
              <span className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/80 text-xs tracking-wide">{t('handmade.tags.tandoori')}</span>
              <span className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/80 text-xs tracking-wide">{t('handmade.tags.freshDaily')}</span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HandmadeSection;
